// A TELA EM DIÁLOGO da conversa: uma página inteira (resultados, tarifas) que
// abre por cima do que estiver em cena — o `Dialog` do ui-kit com
// `variant="screen"`. Trap de foco, Esc, trava de rolagem e o foco de volta a
// quem abriu são do Base UI.
//
// As CAMADAS feitas à mão que abrem de dentro da tela (a gaveta de políticas
// do carro, o detalhe do voo) moram DENTRO do popup, nunca como irmãs dele: o
// Base UI esconde do leitor de tela tudo o que fica fora (`aria-hidden`), o
// backdrop dele engole o clique, e o popup está em `--z-popup`. Quem monta por
// portal pede o nó em `useLayerHost`.
//
// O Esc com uma camada dessas em cena é dela: o Base UI pega o Esc no popup e
// para a propagação, então o fechamento é cancelado e o evento segue até o
// listener da camada. Um Esc que alguém dentro da tela já tratou
// (`preventDefault`, como a faixa de busca expandida do hotel, que recolhe)
// também não fecha a tela — o Base UI não olha `defaultPrevented`. A regra vale para QUALQUER `[role="dialog"]` dentro do
// popup — uma camada nova precisa existir só enquanto aberta e ouvir o próprio
// Esc, senão a tela deixa de fechar pelo Esc.
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { Dialog, DialogContent } from './dialog';

const LayerHostContext = createContext<HTMLElement | null>(null);

/** Onde uma camada interna monta por portal: o popup da tela em diálogo em
 *  volta, ou o `body` fora de uma. */
export function useLayerHost(): HTMLElement {
  return useContext(LayerHostContext) ?? document.body;
}

export function ScreenDialog({
  label,
  onClose,
  className,
  attributes,
  popupRef: externalRef,
  initialFocus,
  escapeCloses,
  children,
}: Readonly<{
  /** O nome acessível da tela. */
  label: string;
  onClose: () => void;
  className?: string;
  /** Marcadores `data-*` do popup, que o host procura no documento. */
  attributes?: Readonly<Record<`data-${string}`, string>>;
  /** O nó do popup, para quem o lê de fora (a pilha lógica do hotel). */
  popupRef?: RefObject<HTMLDivElement | null>;
  /** O foco de entrada, quando não é o próprio popup (o ✕ do explorer). Sem
   *  o nó em cena, cai no popup. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Veto do Esc: `false` mantém a tela aberta (a tela de tarifas do explorer,
   *  onde fechar descartaria a busca e os voos escolhidos). */
  escapeCloses?: () => boolean;
  children: ReactNode;
}>): ReactNode {
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  // Estável: um callback novo a cada render faria o React soltar e religar a
  // ref (e o `setHost`) em todo commit.
  const attach = useCallback(
    (element: HTMLDivElement | null) => {
      popupRef.current = element;
      if (externalRef) externalRef.current = element;
      setHost(element);
    },
    [externalRef],
  );
  return (
    <Dialog
      open
      onOpenChange={(open, details) => {
        if (open) return;
        if (details.reason === 'escape-key') {
          if (details.event.defaultPrevented) {
            details.cancel();
            return;
          }
          if (popupRef.current?.querySelector('[role="dialog"]')) {
            details.cancel();
            details.allowPropagation();
            return;
          }
          if (escapeCloses && !escapeCloses()) {
            details.cancel();
            return;
          }
        }
        onClose();
      }}
    >
      <DialogContent
        ref={attach}
        variant="screen"
        // Sem `initialFocus`, o foco entra no próprio popup: o leitor de tela
        // anuncia a tela antes do primeiro controle.
        initialFocus={() => initialFocus?.current ?? popupRef.current}
        className={className}
        aria-label={label}
        {...attributes}
      >
        <LayerHostContext.Provider value={host}>{children}</LayerHostContext.Provider>
      </DialogContent>
    </Dialog>
  );
}
